import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Timer, Lock, Unlock, AlertTriangle, BarChart3, Trophy,
  Users, Calendar, ChevronRight, ChevronDown, ArrowLeft,
  CheckCircle2, CircleDashed,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScoreEntryPanel } from '@/components/delegate/ScoreEntryPanel';
import { StandingsPanel } from '@/components/delegate/StandingsPanel';
import { useRoundConfigs, useEventStandings, useUpsertRoundConfig } from '@/api/extensions/useScoringOps';
import { useDrawStatus, useDrawTeams, useLockDraw, useDaySheet } from '@/api/extensions/useDrawOps';
import { useJackpotsList, useJackpotDetail } from '@/api/extensions/useJackpotOps';
import { useRodeosList } from '@/api/extensions/useRodeoOps';
import { useEventDetail } from '@/api/extensions/useRodeoEvents';
import {
  useRodeoEventPerformances,
  useRodeoScoringEligibility,
  useSetEventScoringState,
  useResetEventScores,
  useSetShowResults,
  useSetCurrentLiveEvent,
  useSetRunOrderMode,
  runOrderNote,
  RUN_ORDER_OPTIONS,
  type RodeoEventPerformance,
  type EventEligibility,
  type RunOrderMode,
} from '@/api/extensions/useRodeoScoringOps';
import type { RoundConfig, StandingEntry } from '@/api/extensions/useScoringOps';
import { formatDateRange } from '@/utils/formatters';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import {
  type ScheduleTab,
  isPastSchedule,
  sortScheduleByTab,
} from '@/utils/scheduleTabs';
import { createScheduleListState } from '@/utils/scheduleListState';

type PageTab = 'overview' | 'scoring' | 'results';

export default function DelegateLiveScoringPage() {
  const navigate = useNavigate();
  const { eventId: routeEventId } = useParams<{ eventId?: string }>();
  // Back-compat: also honor the legacy ?eventId=... query string and
  // upgrade it to the route param so any old bookmarks / external links
  // keep working without breakage.
  const [searchParams] = useSearchParams();
  const legacyQueryEventId = searchParams.get('eventId');
  const eventId = routeEventId ?? legacyQueryEventId ?? null;

  const [activeTab, setActiveTab] = useState<PageTab>(eventId ? 'scoring' : 'overview');
  const [activeRound, setActiveRound] = useState(1);
  /** Rodeo only: which performance the operator is scoring within. The
   *  same registrations run every round of one performance, so we hold
   *  the perf separately from the round and only switch perfs manually. */
  const [activePerformanceIdx, setActivePerformanceIdx] = useState(0);
  const [standingsRound, setStandingsRound] = useState<number | null>(null);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  useEffect(() => {
    if (!routeEventId && legacyQueryEventId) {
      navigate(`/delegate/live-scoring/${legacyQueryEventId}`, { replace: true });
    }
  }, [routeEventId, legacyQueryEventId, navigate]);

  useEffect(() => {
    if (eventId) setActiveTab('scoring');
  }, [eventId]);

  const handleSelectEvent = (id: string) => {
    navigate(`/delegate/live-scoring/${id}`);
    setActiveTab('overview');
  };

  const handleBack = () => {
    navigate('/delegate/live-scoring');
  };

  const { data: roundConfigsResponse, isLoading: configsLoading } = useRoundConfigs(eventId ?? '');
  const { data: drawStatusData } = useDrawStatus(eventId ?? '');
  const { data: teamsData } = useDrawTeams(eventId ?? '');
  const { data: standingsData } = useEventStandings(eventId ?? '');
  const { data: eventDetail } = useEventDetail(eventId ?? '');
  const lockDraw = useLockDraw();
  const upsertConfig = useUpsertRoundConfig();
  const setRunOrder = useSetRunOrderMode();

  // Rodeo events (events.rodeo_id != null) drive scoring through a polymorphic
  // backend that synthesizes team rows from draft_draw_entries + event_registrations
  // and treats each performance_event as one "round" in the existing UI. The
  // jackpot lock-draw gate is bypassed; rodeo eligibility comes from draw_executions
  // (must be at least published for sandbox, finalized for live).
  const rodeoId: string | null = (eventDetail as any)?.rodeo_id ?? null;
  const isRodeo = !!rodeoId;
  const { data: rodeoPerfsResp } = useRodeoEventPerformances(isRodeo ? eventId ?? undefined : undefined);
  const rodeoPerformances: RodeoEventPerformance[] = (rodeoPerfsResp as any)?.data ?? [];
  // Finals rodeo: every contestant runs EVERY performance, and each
  // performance the event's rounds are tied to IS one round. The board shows
  // one row of round tabs instead of the performance x rounds grid, and each
  // tab moves the performance and the round together.
  const isFinals = isRodeo && (rodeoPerfsResp as any)?.is_finals === true;
  const { data: rodeoEligResp } = useRodeoScoringEligibility(isRodeo ? rodeoId ?? undefined : undefined);
  const rodeoEventEligibility = isRodeo
    ? (rodeoEligResp?.events ?? []).find((e) => e.event_id === eventId) ?? null
    : null;

  const roundConfigs: RoundConfig[] = (roundConfigsResponse as any)?.data ?? (roundConfigsResponse as unknown as any[]) ?? [];
  // Event identity for the header — operators need to see WHICH event/discipline
  // they're scoring (especially for rodeos with many events per performance).
  const eventName = (eventDetail as any)?.event_name as string | undefined;
  const eventTypeLabel = (eventDetail as any)?.trig_event_type_name as string | undefined;
  // Organization + rodeo names for the header. The events API doesn't expose
  // the trig_organization_name / trig_rodeo_name columns (public:false in the
  // entity config), so read them from the day-sheet enrichment instead — the
  // same source the draw-management header uses (server-side admin select, so
  // it bypasses the field-level restriction).
  const { data: daySheet } = useDaySheet(eventId ?? '');
  const orgName = (daySheet as any)?.organization_name as string | undefined;
  const rodeoNameLabel = (daySheet as any)?.rodeo_name as string | undefined;
  const scoring = resolveEventScoring(eventDetail as any);
  const eventTotalRounds = Number((eventDetail as any)?.total_rounds) || 3;
  // For rodeos: every performance has the same N rounds (events.total_rounds).
  // For jackpots: read from rounds rows, falling back to 3.
  // A finals event's round count is however many performances its rounds are
  // tied to — events.total_rounds is not what the draw seated.
  const finalsMaxRound = rodeoPerformances.reduce(
    (max, perf) => Math.max(max, Number(perf.round_number) || 0),
    0,
  );
  const maxRound = isRodeo
    ? (isFinals && finalsMaxRound > 0 ? finalsMaxRound : Math.max(eventTotalRounds, 1))
    : (roundConfigs.length > 0
        ? Math.max(...roundConfigs.map((r) => r.round_number))
        : 3);
  const activePerformanceEventId = isRodeo
    ? rodeoPerformances[activePerformanceIdx]?.id ?? null
    : null;
  // Finals: run order is stored per ROUND, and on a finals board a round is a
  // performance, so read it off the round the active tab runs.
  const activeRunOrderMode: RunOrderMode =
    rodeoPerformances.find((perf) => Number(perf.round_number) === activeRound)?.run_order_mode ??
    'draw';
  const activeRunOrderNote = isFinals ? runOrderNote(activeRunOrderMode) : null;
  // Finals: the active round is a property of the active performance, so keep
  // the two in step on load (and if the round configs are re-tied underneath).
  useEffect(() => {
    if (!isFinals) return;
    const round = Number(rodeoPerformances[activePerformanceIdx]?.round_number) || 0;
    if (round > 0 && round !== activeRound) setActiveRound(round);
  }, [isFinals, rodeoPerformances, activePerformanceIdx, activeRound]);

  const drawStatus = drawStatusData as any;
  const isDrawExecuted = isRodeo
    ? rodeoEventEligibility?.sandbox_allowed === true || rodeoEventEligibility?.live_allowed === true
    : (drawStatus?.draw_executed ?? drawStatus?.is_executed ?? false);
  // Rodeos use the draw_executions publish/finalize lifecycle in lieu of the
  // jackpot lock flag. If the draw is at least published, treat the rodeo as
  // "locked" so the scoring tab unblocks.
  const isDrawLocked = isRodeo
    ? rodeoEventEligibility?.sandbox_allowed === true
    : (drawStatus?.draw_locked ?? drawStatus?.is_locked ?? false);
  const teamList = (teamsData as any)?.data ?? (teamsData as any[]) ?? [];
  const standingsList: StandingEntry[] = (standingsData as any)?.data ?? (standingsData as unknown as any[]) ?? [];

  const activeTeams = teamList.filter((t: any) => t.status === 'active').length;
  const eliminatedTeams = teamList.filter((t: any) => t.status === 'eliminated').length;
  const totalTeams = teamList.length;

  const handleLockDraw = useCallback(async () => {
    if (!eventId) return;
    await lockDraw.mutateAsync({ eventId, lock: true });
    setShowLockConfirm(false);
  }, [eventId, lockDraw]);

  // If no event selected, show the jackpot landing list
  if (!eventId) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Timer className="h-6 w-6" />
            Live Scoring
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Select an event to enter scores and view results
          </p>
        </div>
        <JackpotEventList onSelectEvent={handleSelectEvent} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <button
          onClick={handleBack}
          className="text-blue-500 hover:text-blue-400 text-sm flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Event List
        </button>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Timer className="h-6 w-6" />
              Live Scoring
            </h1>
            {(orgName || rodeoNameLabel) && (
              <p className="text-sm font-medium text-foreground pl-8">
                {[orgName, rodeoNameLabel].filter(Boolean).join(' · ')}
              </p>
            )}
            {(eventTypeLabel || eventName) && (
              <div className="flex items-center gap-2 flex-wrap pl-8">
                {eventTypeLabel && (
                  <Badge
                    variant="secondary"
                    className="text-xs font-semibold"
                  >
                    {eventTypeLabel}
                  </Badge>
                )}
                {eventName && eventName !== eventTypeLabel && (
                  <span className="text-sm text-muted-foreground">{eventName}</span>
                )}
                <Badge variant="outline" className="text-[10px] font-normal">
                  {scoring.isScored ? 'High score wins' : 'Fast time wins'}
                </Badge>
              </div>
            )}
          </div>
          <Badge
            variant={isDrawLocked ? 'default' : 'destructive'}
            className="text-xs gap-1"
          >
            {isDrawLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
            {isDrawLocked ? 'Draw Locked' : 'Draw Unlocked'}
          </Badge>
        </div>
      </div>

      {/* Status Bar */}
      {/* Rodeos never eliminate contestants/teams, so the Eliminated counter is
          meaningless for them — hide it and tighten the grid to avoid an empty cell. */}
      {isDrawExecuted && (
        <div className={`grid gap-3 ${isRodeo ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <StatCard label="Total Teams" value={totalTeams} />
          <StatCard label="Active" value={activeTeams} color="text-green-500" />
          {!isRodeo && (
            <StatCard label="Eliminated" value={eliminatedTeams} color="text-red-500" />
          )}
        </div>
      )}

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as PageTab)}
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="scoring">
            <Timer className="h-3.5 w-3.5 mr-1" />
            Live Scoring
          </TabsTrigger>
          <TabsTrigger value="results">
            <Trophy className="h-3.5 w-3.5 mr-1" />
            Results
          </TabsTrigger>
        </TabsList>

        {/* ─── Overview Tab ─────────────────────────────── */}
        <TabsContent value="overview">
          <div className="space-y-4">
            <div className={`grid grid-cols-2 gap-3 ${isRodeo ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
              <StatCard label="Total Teams" value={totalTeams} />
              <StatCard label="Active" value={activeTeams} />
              {!isRodeo && (
                <StatCard label="Eliminated" value={eliminatedTeams} />
              )}
              <StatCard label="Rounds" value={roundConfigs.length} />
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {!isDrawLocked && isDrawExecuted && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setShowLockConfirm(true)}
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    Lock Draw to Begin Scoring
                  </Button>
                )}
                {isDrawLocked && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setActiveTab('scoring')}
                  >
                    <Timer className="h-4 w-4 mr-1" />
                    Start Scoring
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveTab('results')}
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  View Standings
                </Button>
              </CardContent>
            </Card>

            {standingsList.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">Current Top 5</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {standingsList.slice(0, 5).map((s) => (
                      <div
                        key={s.team_id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span>
                          <span className="font-mono text-muted-foreground mr-2">
                            {s.placement}.
                          </span>
                          #{s.team_number} — {s.member_names?.join(' / ') ?? '—'}
                        </span>
                        <span className="font-mono text-xs">
                          {s.successful_catches} catches
                          {s.total_time != null && ` · ${Number(s.total_time).toFixed(timedScoreDecimals(scoring.isBarrelRacing))}s`}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ─── Live Scoring Tab ────────────────────────── */}
        <TabsContent value="scoring">
          {isRodeo && rodeoId && eventId ? (
            <RodeoScoringControlBar
              eventId={eventId}
              eligibility={rodeoEventEligibility}
            />
          ) : null}
          {!isDrawExecuted ? (
            <LockWarning
              message={
                isRodeo
                  ? (rodeoEventEligibility?.blocked_reason ??
                      'Draw must be at least published before scoring can begin.')
                  : 'Draw has not been executed yet. Execute the draw before scoring.'
              }
              icon={<AlertTriangle className="h-12 w-12 text-yellow-500" />}
            />
          ) : !isDrawLocked && !isRodeo ? (
            <LockWarning
              message="Draw must be locked before scoring can begin. This prevents changes to team assignments during scoring."
              icon={<Unlock className="h-12 w-12 text-yellow-500" />}
              action={
                <Button
                  onClick={() => setShowLockConfirm(true)}
                  disabled={lockDraw.isPending}
                >
                  <Lock className="h-4 w-4 mr-1" />
                  {lockDraw.isPending ? 'Locking...' : 'Lock Draw Now'}
                </Button>
              }
            />
          ) : (
            <div className="space-y-4">
              {configsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <div>
                  <div className="rounded-lg border p-3 bg-muted/30 flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      {isFinals ? (
                        rodeoPerformances.map((perf, perfIdx) => {
                          const isActiveRound = perfIdx === activePerformanceIdx;
                          return (
                            <button
                              key={perf.id}
                              onClick={() => {
                                setActivePerformanceIdx(perfIdx);
                                setActiveRound(Number(perf.round_number) || perfIdx + 1);
                              }}
                              className={`px-3 py-1.5 rounded-md font-medium text-sm transition-colors border ${
                                isActiveRound
                                  ? 'bg-blue-600 text-white border-blue-700'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/80 border-border'
                              }`}
                            >
                              {perf.round_label ?? `Round ${perf.round_number}`}
                              {perf.performance_name ? ` — ${perf.performance_name}` : ''}
                              {perf.is_scoring_published_live && (
                                <span className="ml-1 px-1 py-0 bg-emerald-500 text-white text-[9px] rounded">LIVE</span>
                              )}
                            </button>
                          );
                        })
                      ) : isRodeo ? (
                        rodeoPerformances.map((perf, perfIdx) => {
                          const isActivePerf = perfIdx === activePerformanceIdx;
                          return (
                            <div
                              key={perf.id}
                              className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${
                                isActivePerf
                                  ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-300 dark:border-blue-700'
                                  : ''
                              }`}
                            >
                              <span
                                className={`text-xs font-bold uppercase tracking-wide pr-1 ${
                                  isActivePerf
                                    ? 'text-blue-700 dark:text-blue-300'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {perf.performance_name ?? `Perf ${perfIdx + 1}`}
                                {perf.is_scoring_published_live && (
                                  <span className="ml-1 px-1 py-0 bg-emerald-500 text-white text-[9px] rounded">LIVE</span>
                                )}
                              </span>
                              {Array.from({ length: maxRound }, (_, i) => i + 1).map((r) => {
                                const isActiveCell = isActivePerf && r === activeRound;
                                return (
                                  <button
                                    key={r}
                                    onClick={() => {
                                      setActivePerformanceIdx(perfIdx);
                                      setActiveRound(r);
                                    }}
                                    className={`px-2.5 py-1 rounded-md font-medium text-xs transition-colors border ${
                                      isActiveCell
                                        ? 'bg-blue-600 text-white border-blue-700'
                                        : 'bg-muted text-muted-foreground hover:bg-muted/80 border-border'
                                    }`}
                                  >
                                    R{r}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })
                      ) : (
                        Array.from({ length: maxRound }, (_, i) => i + 1).map((r) => {
                          const config = roundConfigs.find((c) => c.round_number === r);
                          return (
                            <button
                              key={r}
                              onClick={() => setActiveRound(r)}
                              className={`px-3 py-1.5 rounded-md font-medium text-sm transition-colors border ${
                                activeRound === r
                                  ? 'bg-blue-600 text-white border-blue-700'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/80 border-border'
                              }`}
                            >
                              R{r}
                              {config?.is_short_go && (
                                <span className="ml-1 px-1 py-0 bg-orange-500 text-white text-[10px] rounded">S</span>
                              )}
                              {config?.is_progressive && (
                                <span className="ml-1 px-1 py-0 bg-green-500 text-white text-[10px] rounded">P</span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{(teamsData as any)?.data?.length ?? 0} teams</span>
                      {/* Finals only: each round's run order is the
                          association's call, not the draw. Every other rodeo
                          runs the drawn order and has no picker. */}
                      {isFinals && (
                        <>
                          <span className="text-muted-foreground/50">-</span>
                          <span className="text-xs">Run order:</span>
                          <select
                            value={activeRunOrderMode}
                            disabled={setRunOrder.isPending}
                            onChange={(e) => {
                              setRunOrder.mutate({
                                eventId: eventId!,
                                roundNumber: activeRound,
                                run_order_mode: e.target.value as RunOrderMode,
                              });
                            }}
                            className="px-2 py-1 border rounded text-sm bg-background"
                          >
                            {RUN_ORDER_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                      {(() => {
                        const activeConfig = roundConfigs.find(c => c.round_number === activeRound);
                        if (!activeConfig?.is_short_go) return null;
                        return (
                          <>
                            <span className="text-muted-foreground/50">-</span>
                            <span className="text-xs">Short Go Order:</span>
                            <select
                              value={activeConfig.short_go_order ?? 'slowest_first'}
                              onChange={async (e) => {
                                await upsertConfig.mutateAsync({
                                  eventId: eventId!,
                                  roundNumber: activeRound,
                                  is_short_go: true,
                                  short_go_order: e.target.value as 'slowest_first' | 'fastest_first',
                                });
                              }}
                              className="px-2 py-1 border rounded text-sm bg-background"
                            >
                              <option value="slowest_first">Slowest → Fastest</option>
                              <option value="fastest_first">Fastest → Slowest</option>
                            </select>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {activeRunOrderNote && (
                    <p className="text-xs text-muted-foreground -mt-2 mb-3 pl-1">
                      {activeRunOrderNote} &mdash; the draw is unchanged.
                    </p>
                  )}

                  <ScoreEntryPanel
                    // Re-mount when the active performance changes so all
                    // internal scoring state (active team idx, form state)
                    // resets cleanly — a contestant in perf 1 has no place
                    // in perf 2.
                    key={activePerformanceEventId ?? 'jackpot'}
                    eventId={eventId}
                    roundNumber={activeRound}
                    maxRounds={maxRound}
                    performanceEventId={activePerformanceEventId ?? undefined}
                    onNextRound={() => {
                      // Auto-advance only within the current performance.
                      // The user said: at the last round of a performance,
                      // stay put so the operator can review — the next
                      // performance is typically the next day. On a finals
                      // board every round IS its own performance, so that
                      // rule means never auto-advancing there.
                      if (isFinals) return;
                      if (activeRound < maxRound) setActiveRound(activeRound + 1);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ─── Results Tab ──────────────────────────────── */}
        <TabsContent value="results">
          <StandingsPanel
            eventId={eventId}
            roundFilter={standingsRound}
            onRoundFilterChange={setStandingsRound}
            maxRounds={maxRound}
          />
        </TabsContent>
      </Tabs>

      {/* Lock Draw Confirmation Dialog */}
      <Dialog open={showLockConfirm} onOpenChange={setShowLockConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock Draw for Scoring</DialogTitle>
            <DialogDescription>
              Lock the draw to enable live scoring? This will prevent changes to teams.
              You can unlock it later from the Manage Draw page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLockConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleLockDraw} disabled={lockDraw.isPending}>
              <Lock className="h-4 w-4 mr-1" />
              {lockDraw.isPending ? 'Locking...' : 'Lock Draw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Rodeo control bar ─────────────────────────────────────────────

function RodeoScoringControlBar({
  eventId,
  eligibility,
}: {
  eventId: string;
  eligibility: EventEligibility | null;
}) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const scoringState = eligibility?.scoring_state ?? 'not_started';
  const isLive = scoringState === 'live';
  // The currently-active mode the reset button + entry panel operate on.
  const currentMode: 'sandbox' | 'live' = isLive ? 'live' : 'sandbox';
  const showResults = !!eligibility?.show_results;
  const isCurrentEvent = !!eligibility?.is_current_live_event;
  const liveAllowed = !!eligibility?.live_allowed;

  const setScoringState = useSetEventScoringState();
  const resetScores = useResetEventScores();
  const setShowResults = useSetShowResults();
  const setCurrentLiveEvent = useSetCurrentLiveEvent();

  return (
    <div className="rounded-lg border p-3 bg-muted/30 mb-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={isLive ? 'default' : 'secondary'} className="text-xs">
            {isLive ? 'LIVE scoring' : `SCORING: ${scoringState.replace('_', ' ').toUpperCase()}`}
          </Badge>
          {eligibility?.blocked_reason ? (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {eligibility.blocked_reason}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sandbox ⇄ live for THIS event — never wipes the other mode's scores. */}
          {isLive ? (
            <Button
              size="sm"
              variant="outline"
              disabled={setScoringState.isPending}
              onClick={() =>
                setScoringState.mutate({ eventId, scoring_state: 'sandbox' })
              }
              title="Pause this event back to sandbox — live scores are kept"
            >
              Pause to sandbox
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={setScoringState.isPending || !liveAllowed}
              onClick={() => setScoringState.mutate({ eventId, scoring_state: 'live' })}
              title={liveAllowed ? 'Start live scoring for this event' : eligibility?.blocked_reason ?? 'Finalize the draw first'}
            >
              Go live
            </Button>
          )}

          {/* Show-results toggle replaces the old "Publish live" button. */}
          <Button
            size="sm"
            variant={showResults ? 'default' : 'outline'}
            disabled={setShowResults.isPending}
            onClick={() => setShowResults.mutate({ eventId, show_results: !showResults })}
            title="Show or hide the results card on the event detail page"
          >
            {showResults ? 'Results shown' : 'Show results'}
          </Button>

          {/* Current-live-event toggle for the rodeo public live page. */}
          <Button
            size="sm"
            variant={isCurrentEvent ? 'default' : 'outline'}
            disabled={setCurrentLiveEvent.isPending}
            onClick={() =>
              setCurrentLiveEvent.mutate({ eventId, is_current_live_event: !isCurrentEvent })
            }
            title="Make this the event shown on the rodeo's public live page"
          >
            {isCurrentEvent ? 'Live page: ON' : 'Live page: OFF'}
          </Button>

          {/* Mode-aware reset — clears only the current mode's scores. */}
          <Button
            size="sm"
            variant="outline"
            disabled={resetScores.isPending}
            onClick={() => setShowResetConfirm(true)}
            title={`Delete this event's ${currentMode} scores`}
          >
            Reset {currentMode}
          </Button>
        </div>
      </div>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset {currentMode} scores for this event?</DialogTitle>
            <DialogDescription>
              This deletes every <strong>{currentMode}</strong>-mode score recorded for this event.
              {currentMode === 'sandbox'
                ? ' Live results are not affected.'
                : ' Sandbox results are not affected.'}{' '}
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  await resetScores.mutateAsync({ eventId, mode: currentMode });
                  setShowResetConfirm(false);
                } catch {
                  // mutation surfaces its own error; keep the dialog open so the operator can retry
                }
              }}
              disabled={resetScores.isPending}
            >
              {resetScores.isPending ? 'Resetting…' : `Reset ${currentMode}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Jackpot/Event Landing List ─────────────────────────────────

// Persists the landing list's expanded-card set across the event-scoring
// drill-in. Selecting an event routes to /delegate/live-scoring/:eventId,
// which swaps this list out for the scoring view (unmounting the list and
// its cards). Pure useState in each card would reset every expansion on the
// way back; sessionStorage scopes the memory to the current tab only (a full
// refresh wipes it). This mirrors the Manage Draws list behavior.
const liveScoringViewState = createScheduleListState('delegate-live-scoring');

const loadExpandedCardIds = liveScoringViewState.loadExpandedIds;
const saveExpandedCardIds = liveScoringViewState.saveExpandedIds;

// The Upcoming/Past tab is persisted alongside the expanded-card set so that
// drilling into an event's scoring view (which remounts this list on the way
// back) doesn't snap the operator back to the default Upcoming tab.
const loadScheduleTab = liveScoringViewState.loadTab;
const saveScheduleTab = liveScoringViewState.saveTab;

function JackpotEventList({ onSelectEvent }: { onSelectEvent: (id: string) => void }) {
  const { data: jackpotsResponse, isLoading: jackpotsLoading } = useJackpotsList({ pageSize: 50 });
  const { data: rodeosResponse, isLoading: rodeosLoading } = useRodeosList({ page_size: 50, is_published: true });
  const jackpots = (jackpotsResponse as any)?.data ?? [];
  const rodeos = (rodeosResponse as any)?.data ?? [];
  const combined = [...rodeos, ...jackpots];
  const isLoading = jackpotsLoading || rodeosLoading;

  // Upcoming vs past tab — mirrors the Manage Draws list. Upcoming is the
  // default so operators land on events that still need scoring; the Past tab
  // keeps completed rodeos/jackpots reachable for reference. Both the
  // finished-check and the sort comparator come from @/utils/scheduleTabs so
  // this stays in lock-step with the Draws screen.
  const [scheduleTab, setScheduleTab] = useState<ScheduleTab>(() =>
    loadScheduleTab(),
  );
  const handleScheduleTabChange = (tab: ScheduleTab) => {
    setScheduleTab(tab);
    saveScheduleTab(tab);
  };

  const tabCounts = useMemo(() => {
    let upcoming = 0;
    let past = 0;
    for (const item of combined) {
      if (isPastSchedule(item)) past += 1;
      else upcoming += 1;
    }
    return { upcoming, past };
  }, [combined]);

  const visibleCards = useMemo(
    () =>
      sortScheduleByTab(
        combined.filter((item: any) =>
          scheduleTab === 'past'
            ? isPastSchedule(item)
            : !isPastSchedule(item),
        ),
        scheduleTab,
      ),
    [combined, scheduleTab],
  );

  // Expanded-card state lives here (not in each JackpotCard) so it can be
  // persisted to sessionStorage and survive the drill-in/return remount.
  const [expandedCardIds, setExpandedCardIds] = useState<Set<string>>(
    () => loadExpandedCardIds(),
  );
  const toggleCardExpanded = (cardId: string, next: boolean) => {
    setExpandedCardIds((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(cardId);
      else updated.delete(cardId);
      saveExpandedCardIds(updated);
      return updated;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-lg" />
        ))}
      </div>
    );
  }

  if (combined.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <Timer className="h-16 w-16 text-muted-foreground/30" />
        <p className="text-muted-foreground">No jackpots or rodeos available for live scoring</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upcoming / Past tabs — switch between events still ahead and
          completed ones. Upcoming is the default so only relevant work shows. */}
      <div className="flex items-center gap-1 border-b border-border">
        {(
          [
            ['upcoming', 'Upcoming', tabCounts.upcoming],
            ['past', 'Past', tabCounts.past],
          ] as [ScheduleTab, string, number][]
        ).map(([id, label, count]) => (
          <button
            key={id}
            onClick={() => handleScheduleTabChange(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              scheduleTab === id
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-400'
            }`}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {visibleCards.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/50 p-8 text-center">
          <Timer className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            {scheduleTab === 'past'
              ? 'No past rodeos or jackpots'
              : 'No upcoming rodeos or jackpots'}
          </p>
        </div>
      ) : (
        visibleCards.map((jp: any) => (
          <JackpotCard
            key={jp.id}
            jackpot={jp}
            onSelectEvent={onSelectEvent}
            expanded={expandedCardIds.has(jp.id)}
            onExpandedChange={(next) => toggleCardExpanded(jp.id, next)}
          />
        ))
      )}
    </div>
  );
}

type EventReadiness = 'live_ready' | 'sandbox_ready' | 'not_ready';

interface EventReadinessInfo {
  state: EventReadiness;
  label: string;
  reason: string | null;
  /** Per-event scoring phase (not_started | sandbox | live | completed). */
  scoringState: string;
  /** Whether the event's results card is published to contestants. Rodeo-only. */
  showResults: boolean;
  /** Contestants with a recorded run so far (live-scoring progress badge). */
  scoredCount: number;
  /** Contestants drawn into the event's latest execution (badge denominator). */
  totalCount: number;
}

function JackpotCard({
  jackpot,
  onSelectEvent,
  expanded,
  onExpandedChange,
}: {
  jackpot: any;
  onSelectEvent: (id: string) => void;
  // Expand state is owned by JackpotEventList and persisted to sessionStorage
  // so it survives the drill-in/return remount (see EXPANDED_CARDS_STORAGE_KEY).
  expanded: boolean;
  onExpandedChange: (next: boolean) => void;
}) {
  const isRodeo = jackpot.rodeo_or_jackpot === 'rodeo';
  const { data: detail, isLoading } = useJackpotDetail(jackpot.id);
  // Rodeo readiness reads from draw_executions via /rodeo-scoring-ops; jackpot
  // events fall back to useDrawStatus per row (handled inside EventRow).
  const { data: rodeoElig } = useRodeoScoringEligibility(isRodeo ? jackpot.id : undefined);
  const events: any[] = (detail as any)?.events ?? [];

  const open = expanded;

  // Build a readiness map per event so we can sort + count without
  // re-fetching downstream.
  const readinessByEvent: Map<string, EventReadinessInfo> = new Map();
  if (isRodeo) {
    for (const e of rodeoElig?.events ?? []) {
      readinessByEvent.set(e.event_id, mapRodeoEligibility(e));
    }
  }

  // For jackpots we don't have a per-jackpot rollup endpoint, so the row
  // component fills its own readiness. We still render the card header
  // counts using whatever we know; jackpot rows will live-update once
  // their useDrawStatus resolves.
  const rolledUp = isRodeo
    ? rollupReadiness(
        events
          .map((ev: any) => readinessByEvent.get(ev.id)?.state)
          .filter((s): s is EventReadiness => !!s),
      )
    : null;

  if (!isLoading && events.length === 0) return null;

  return (
    <div className="rounded-lg border overflow-hidden">
      <button
        type="button"
        onClick={() => onExpandedChange(!open)}
        className="w-full bg-gray-700 dark:bg-gray-800 px-5 py-4 text-white flex items-start justify-between gap-3 text-left hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-start gap-2 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 mt-1 shrink-0 text-gray-300" />
          ) : (
            <ChevronRight className="h-4 w-4 mt-1 shrink-0 text-gray-300" />
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-lg flex items-center gap-2">
              {jackpot.name}
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
                  isRodeo ? 'bg-purple-600' : 'bg-blue-600'
                }`}
              >
                {isRodeo ? 'Rodeo' : 'Jackpot'}
              </span>
            </h3>
            {jackpot.organization_name && (
              <p className="text-gray-300 text-sm">{jackpot.organization_name}</p>
            )}
          </div>
        </div>
        <div className="text-right text-sm shrink-0">
          {rolledUp ? (
            <div className="flex items-center justify-end gap-2 text-xs">
              {rolledUp.live_ready > 0 ? (
                <span className="flex items-center gap-1 text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" />
                  {rolledUp.live_ready} live
                </span>
              ) : null}
              {rolledUp.sandbox_ready > 0 ? (
                <span className="flex items-center gap-1 text-amber-300">
                  <AlertTriangle className="h-3 w-3" />
                  {rolledUp.sandbox_ready} sandbox
                </span>
              ) : null}
              {rolledUp.not_ready > 0 ? (
                <span className="flex items-center gap-1 text-gray-400">
                  <CircleDashed className="h-3 w-3" />
                  {rolledUp.not_ready} not ready
                </span>
              ) : null}
            </div>
          ) : (
            events.length > 0 && (
              <p className="text-gray-300">
                {events.length} event{events.length !== 1 ? 's' : ''}
              </p>
            )
          )}
          {(jackpot.start_date || jackpot.end_date) && (
            <p className="text-gray-400 flex items-center gap-1 justify-end mt-0.5">
              <Calendar className="h-3 w-3" />
              {formatDateRange(jackpot.start_date, jackpot.end_date)}
            </p>
          )}
        </div>
      </button>

      {isRodeo && (
        <ShowAllResultsBar events={events} readinessByEvent={readinessByEvent} />
      )}

      {open ? (
        isLoading ? (
          <div className="p-4">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="divide-y">
            {sortEventsByName(events, isRodeo).map((ev: any) => (
              <EventRow
                key={ev.id}
                event={ev}
                isRodeo={isRodeo}
                rodeoReadiness={isRodeo ? readinessByEvent.get(ev.id) ?? null : null}
                onSelect={() => onSelectEvent(ev.id)}
              />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

// Rodeo-header master toggle: flips "show results to contestants" for every
// LIVE event in the rodeo at once. Hidden until at least one event is live
// (the per-event toggle is also live-gated). No bulk endpoint exists, so it
// fans out per-event calls; the eligibility-cache invalidation in the mutation
// keeps every per-row toggle in sync afterward. Reflects "all on" as checked
// and shows a count so a mixed state stays legible.
function ShowAllResultsBar({
  events,
  readinessByEvent,
}: {
  events: any[];
  readinessByEvent: Map<string, EventReadinessInfo>;
}) {
  const setShowResults = useSetShowResults();
  const liveEvents = events.filter(
    (ev) => readinessByEvent.get(ev.id)?.scoringState === 'live',
  );
  if (liveEvents.length === 0) return null;

  const shownCount = liveEvents.filter(
    (ev) => readinessByEvent.get(ev.id)?.showResults,
  ).length;
  const allShown = shownCount === liveEvents.length;

  const handleToggleAll = (next: boolean) => {
    liveEvents.forEach((ev) => {
      if (!!readinessByEvent.get(ev.id)?.showResults !== next) {
        setShowResults.mutate({ eventId: ev.id, show_results: next });
      }
    });
  };

  return (
    // Matches the dark rodeo header band (same bg as the header button above)
    // so the master toggle reads as part of the header — mirroring the Manage
    // Draws screen where it sits in the gray header.
    <div className="px-5 py-2 bg-gray-700 dark:bg-gray-800 border-t border-white/10 flex items-center gap-1.5">
      <Switch
        id="show-all-results-live"
        checked={allShown}
        onCheckedChange={handleToggleAll}
        disabled={setShowResults.isPending}
      />
      <label
        htmlFor="show-all-results-live"
        className="text-xs text-white/90 cursor-pointer whitespace-nowrap"
      >
        Show all results to contestants ({shownCount}/{liveEvents.length})
      </label>
    </div>
  );
}

function mapRodeoEligibility(e: {
  draw_state: string;
  sandbox_allowed: boolean;
  live_allowed: boolean;
  blocked_reason: string | null;
  scoring_state?: string;
  show_results?: boolean;
  scored_count?: number;
  total_count?: number;
}): EventReadinessInfo {
  const scoringState = e.scoring_state ?? 'not_started';
  const showResults = !!e.show_results;
  const scoredCount = e.scored_count ?? 0;
  const totalCount = e.total_count ?? 0;
  if (e.live_allowed) {
    return { state: 'live_ready', label: 'Ready for Live', reason: null, scoringState, showResults, scoredCount, totalCount };
  }
  if (e.sandbox_allowed) {
    return {
      state: 'sandbox_ready',
      label: 'Sandbox Only',
      reason: e.blocked_reason ?? 'Finalize the draw to unlock live scoring.',
      scoringState,
      showResults,
      scoredCount,
      totalCount,
    };
  }
  return {
    state: 'not_ready',
    label: 'Not Ready',
    reason: e.blocked_reason ?? 'Draw is still in draft.',
    scoringState,
    showResults,
    scoredCount,
    totalCount,
  };
}

function rollupReadiness(states: EventReadiness[]) {
  return states.reduce(
    (acc, s) => {
      acc[s] += 1;
      return acc;
    },
    { live_ready: 0, sandbox_ready: 0, not_ready: 0 },
  );
}

// Rodeo event rows always render alphabetically by their display name so the
// list order is stable and predictable for delegates scoring a rodeo. Copies
// the array first so the source order (used elsewhere) is never mutated.
function sortEventsByName(events: any[], isRodeo: boolean): any[] {
  if (!isRodeo) return events;
  return [...events].sort((a, b) =>
    getEventDisplayTitle(a).localeCompare(getEventDisplayTitle(b), undefined, {
      sensitivity: 'base',
    }),
  );
}

function EventRow({
  event,
  isRodeo,
  rodeoReadiness,
  onSelect,
}: {
  event: any;
  isRodeo: boolean;
  rodeoReadiness: EventReadinessInfo | null;
  onSelect: () => void;
}) {
  // Jackpot fallback: derive readiness from useDrawStatus.
  const { data: statusData } = useDrawStatus(isRodeo ? '' : event.id);
  const status = statusData as any;
  const setShowResults = useSetShowResults();
  const readiness: EventReadinessInfo = isRodeo
    ? rodeoReadiness ?? { state: 'not_ready', label: 'Not Ready', reason: null, scoringState: 'not_started', showResults: false, scoredCount: 0, totalCount: 0 }
    : jackpotReadinessFromStatus(status);
  const tone = readinessTone(readiness.state);
  const disabled = readiness.state === 'not_ready';
  // Sandbox vs live mode badge — mirrors how the draw list flags each event.
  const scoringMode = isRodeo ? readiness.scoringState : null;

  return (
    <div
      className={`px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/50 transition-colors border-l-4 ${tone.borderL}`}
    >
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="font-medium text-sm truncate">{getEventDisplayTitle(event)}</span>
        {/* Once the event is live, the LIVE badge says everything — hide the
            now-redundant "Ready for Live" readiness badge so the two don't
            show side by side. The badge still shows for every pre-live state. */}
        {scoringMode !== 'live' && (
          <Badge variant="outline" className={`text-xs gap-1 ${tone.badge}`}>
            {readiness.state === 'live_ready' ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : readiness.state === 'sandbox_ready' ? (
              <AlertTriangle className="h-3 w-3" />
            ) : (
              <CircleDashed className="h-3 w-3" />
            )}
            {readiness.label}
          </Badge>
        )}
        {scoringMode === 'live' ? (
          <Badge className="text-xs bg-red-600 hover:bg-red-600 text-white">LIVE</Badge>
        ) : scoringMode === 'sandbox' ? (
          <Badge variant="secondary" className="text-xs">SANDBOX</Badge>
        ) : scoringMode === 'completed' ? (
          <Badge variant="outline" className="text-xs">COMPLETED</Badge>
        ) : null}
        {/* Scoring-progress badge — how many drawn contestants have been scored.
            Green once everyone is scored, orange while any remain. Rodeo-only
            and hidden until the event has contestants drawn in (total > 0). */}
        {isRodeo && readiness.totalCount > 0 ? (
          <Badge
            className={`text-xs text-white ${
              readiness.scoredCount >= readiness.totalCount
                ? 'bg-emerald-600 hover:bg-emerald-600'
                : 'bg-amber-500 hover:bg-amber-500'
            }`}
            aria-label={`Scored ${readiness.scoredCount} of ${readiness.totalCount}`}
          >
            Scored {readiness.scoredCount}/{readiness.totalCount}
          </Badge>
        ) : null}
        {readiness.reason ? (
          <span className="text-xs text-muted-foreground">{readiness.reason}</span>
        ) : null}
        {!isRodeo && status?.statistics && (
          <span className="text-xs text-blue-500 flex items-center gap-1">
            <Users className="h-3 w-3" />
            {status.statistics.total_teams} teams
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {/* Per-event "Show results" toggle — publishes/hides the results card
            on the contestant-facing event page straight from the list. Only
            appears once the event is live; the same toggle stays on the
            scoring control bar and both reflect the shared eligibility cache. */}
        {isRodeo && scoringMode === 'live' && (
          <div className="flex items-center gap-1.5">
            <Switch
              id={`show-results-${event.id}`}
              checked={readiness.showResults}
              onCheckedChange={(show_results) =>
                setShowResults.mutate({ eventId: event.id, show_results })
              }
              disabled={setShowResults.isPending}
            />
            <label
              htmlFor={`show-results-${event.id}`}
              className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
            >
              Show results
            </label>
          </div>
        )}
        <Button
          size="sm"
          variant="default"
          className={`text-white ${
            disabled
              ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
          onClick={() => {
            if (!disabled) onSelect();
          }}
          disabled={disabled}
          title={disabled ? readiness.reason ?? 'Draw is not ready' : ''}
        >
          <Timer className="h-3.5 w-3.5 mr-1" />
          {disabled ? 'Not Ready' : 'Open Scoring'}
          {!disabled ? <ChevronRight className="h-3.5 w-3.5 ml-1" /> : null}
        </Button>
      </div>
    </div>
  );
}

function jackpotReadinessFromStatus(status: any): EventReadinessInfo {
  const isExecuted = status?.draw_executed ?? status?.is_executed ?? false;
  const isLocked = status?.draw_locked ?? status?.is_locked ?? false;
  if (isLocked)
    return { state: 'live_ready', label: 'Ready for Scoring', reason: null, scoringState: 'not_started', showResults: false, scoredCount: 0, totalCount: 0 };
  if (isExecuted)
    return {
      state: 'sandbox_ready',
      label: 'Draw Not Locked',
      reason: 'Lock the draw before scoring begins.',
      scoringState: 'not_started',
      showResults: false,
      scoredCount: 0,
      totalCount: 0,
    };
  return {
    state: 'not_ready',
    label: 'Draw Not Executed',
    reason: 'Execute the draw before scoring.',
    scoringState: 'not_started',
    showResults: false,
    scoredCount: 0,
    totalCount: 0,
  };
}

function readinessTone(state: EventReadiness): { borderL: string; badge: string } {
  switch (state) {
    case 'live_ready':
      return {
        borderL: 'border-emerald-500',
        badge: 'border-emerald-400 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30',
      };
    case 'sandbox_ready':
      return {
        borderL: 'border-amber-500',
        badge: 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30',
      };
    case 'not_ready':
      return {
        borderL: 'border-gray-400',
        badge: 'border-gray-300 text-muted-foreground',
      };
  }
}

// ─── Helper Components ──────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${color ?? ''}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function LockWarning({
  message,
  icon,
  action,
}: {
  message: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      {icon}
      <p className="text-sm text-muted-foreground text-center max-w-md">{message}</p>
      {action}
    </div>
  );
}
